import React, { useState } from 'react';
import { useUser } from '../../context/UserContext';
import { BrainIcon, CheckIcon, ArrowRightIcon } from 'lucide-react';
const DBTPractice: React.FC = () => {
  const {
    userData
  } = useUser();
  const [currentSkill, setCurrentSkill] = useState(0);
  // DBT skills organized by categories
  const dbtSkills = [{
    category: 'Mindfulness',
    color: 'blue',
    skills: [{
      name: 'Observe',
      description: 'Notice without getting caught in the experience',
      practice: 'Take 5 minutes to simply notice your breath, thoughts, and sensations without trying to change them.'
    }, {
      name: 'Describe',
      description: 'Put words on the experience, without judgment',
      practice: 'Practice naming your emotions and thoughts as they arise: "I notice I m feeling anxious " rather than ": I am anxious . "'
    }, {
      name: 'Participate',
      description: 'Enter wholly into the activity',
      practice: 'Choose one daily activity (like eating or walking) and fully immerse yourself in the experience using all your senses.'
    }]
  }, {
    category: 'Distress Tolerance',
    color: 'green',
    skills: [{
      name: 'TIPP',
      description: 'Temperature, Intense exercise, Paced breathing, Progressive relaxation',
      practice: 'When emotions feel overwhelming, splash cold water on your face, then practice deep breathing for 2 minutes.'
    }, {
      name: 'Radical Acceptance',
      description: 'Accepting life as it is, not as you wish it to be',
      practice: "Identify something you've been fighting against. Practice saying, 'This is what's happening right now. I can't change what's already occurred.'"
    }, {
      name: 'Self-Soothe',
      description: 'Comfort yourself through your five senses',
      practice: 'Create a list of soothing activities for each sense: sight, sound, smell, taste, touch, and engage in one when distressed.'
    }]
  }, {
    category: 'Emotion Regulation',
    color: 'purple',
    skills: [{
      name: 'Check the Facts',
      description: 'Evaluate if your emotional response fits the facts',
      practice: 'When you feel a strong emotion, ask: What event triggered this feeling? What is my interpretation? Are there other ways to look at this situation?'
    }, {
      name: 'Opposite Action',
      description: 'Act opposite to the current emotion',
      practice: 'When feeling fear about something that s not actually threatening gradually approach rather than avoid it .'
    }, {
      name: 'PLEASE',
      description: 'Physical health: eat a balanced diet. Avoid mood-altering drugs. Sleep well. Exercise regularly.',
      practice: 'Choose one aspect of PLEASE to focus on improving this week. Notice how it affects your emotional resilience.'
    }]
  }, {
    category: 'Interpersonal Effectiveness',
    color: 'orange',
    skills: [{
      name: 'DEAR MAN',
      description: 'Describe, express, assert, reinforce, be mindful, appear confident, negotiate.',
      practice: 'Before a difficult conversation, write out what you want to say using the DEAR MAN steps.'
    }, {
      name: 'GIVE',
      description: 'Gentle, Interested, Validate, Easy manner',
      practice: 'In your next conversation practice validating the other person s perspective before sharing your own .'
    }, {
      name: 'FAST',
      description: 'Fair, Apologies (few), Stick to values, Truthful',
      practice: 'Identify one of your core values and practice standing firm on it in a situation where you might usually compromise.'
    }]
  }];
  // Get current garden theme to match skill category
  const getCurrentCategory = () => {
    const gardenToCategory: Record<string, string> = {
      Wisdom: 'Mindfulness',
      Love: 'Emotion Regulation',
      Respect: 'Interpersonal Effectiveness',
      Bravery: 'Distress Tolerance',
      Honesty: 'Mindfulness',
      Humility: 'Interpersonal Effectiveness',
      Truth: 'Emotion Regulation'
    };
    const currentGarden = userData.currentGarden || 'Wisdom';
    return gardenToCategory[currentGarden] || 'Mindfulness';
  };
  const currentCategory = getCurrentCategory();
  const categoryData = dbtSkills.find(cat => cat.category === currentCategory) || dbtSkills[0];
  const skill = categoryData.skills[currentSkill];
  const handleNextSkill = () => {
    setCurrentSkill(prev => (prev + 1) % categoryData.skills.length);
  };
  return <div className="bg-white rounded-xl shadow-md p-6 h-full" data-id="element-121">
      <h2 className="text-xl font-semibold mb-4 flex items-center" data-id="element-122">
        <div className={`bg-${categoryData.color}-100 p-2 rounded-full mr-2`} data-id="element-123">
          <BrainIcon className={`text-${categoryData.color}-600`} size={18} data-id="element-124" />
        </div>
        DBT Practice
      </h2>
      <div className={`bg-${categoryData.color}-50 p-4 rounded-lg mb-4`} data-id="element-125">
        <div className="flex justify-between items-center mb-2" data-id="element-126">
          <h3 className={`font-medium text-${categoryData.color}-800`} data-id="element-127">
            {categoryData.category} Skills
          </h3>
          <span className={`text-xs bg-${categoryData.color}-100 text-${categoryData.color}-800 px-2 py-0.5 rounded-full`} data-id="element-128">
            {currentSkill + 1}/{categoryData.skills.length}
          </span>
        </div>
        <p className="text-gray-600 text-sm mb-1" data-id="element-129">
          These skills align with your {userData.currentGarden || 'Wisdom'}{' '}
          Garden journey
        </p>
      </div>
      <div className="border border-gray-200 rounded-lg p-4 mb-4" data-id="element-130">
        <h3 className="font-medium text-gray-800 mb-1" data-id="element-131">{skill.name}</h3>
        <p className="text-gray-600 text-sm mb-3" data-id="element-132">{skill.description}</p>
        <div className="bg-gray-50 p-3 rounded-lg mb-3" data-id="element-133">
          <h4 className="text-sm font-medium text-gray-700 mb-1" data-id="element-134">
            Today's Practice:
          </h4>
          <p className="text-gray-700" data-id="element-135">{skill.practice}</p>
        </div>
        <div className="flex space-x-2" data-id="element-136">
          <button className={`flex-1 py-2 rounded-lg bg-${categoryData.color}-600 text-white hover:bg-${categoryData.color}-700 flex items-center justify-center`} data-id="element-137">
            <CheckIcon size={16} className="mr-1" data-id="element-138" />
            Complete
          </button>
          <button onClick={handleNextSkill} className="py-2 px-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center" data-id="element-139">
            <ArrowRightIcon size={16} data-id="element-140" />
          </button>
        </div>
      </div>
      <div className="flex justify-between" data-id="element-141">
        {categoryData.skills.map((_, index) => <button key={index} onClick={() => setCurrentSkill(index)} className={`w-2 h-2 rounded-full ${currentSkill === index ? `bg-${categoryData.color}-600` : 'bg-gray-300'}`} data-id="element-142" />)}
      </div>
    </div>;
};
export default DBTPractice;